import { ReactNode, useRef } from "react";

/* ── Inline styles ── */

const spotlightStyle: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  pointerEvents: "none",
  opacity: 0,
  transition: "opacity 0.35s ease",
  background:
    "radial-gradient(circle at var(--mouse-x, 50%) var(--mouse-y, 50%), rgba(255,255,255,0.55), transparent 60%)",
  mixBlendMode: "overlay" as any,
};

interface SpotlightCardProps {
  children: ReactNode;
  className?: string;
  /** Max tilt angle in degrees (default: 10) */
  tilt?: number;
}

const SpotlightCard = ({
  children,
  className = "",
  tilt = 10,
}: SpotlightCardProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const spotRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);
  const currentX = useRef(0);
  const currentY = useRef(0);
  const targetX = useRef(0);
  const targetY = useRef(0);

  const animate = () => {
    currentX.current += (targetX.current - currentX.current) * 0.12;
    currentY.current += (targetY.current - currentY.current) * 0.12;

    if (rootRef.current) {
      rootRef.current.style.transform = `perspective(800px) rotateX(${currentY.current}deg) rotateY(${currentX.current}deg)`;
    }

    if (
      Math.abs(currentX.current - targetX.current) > 0.01 ||
      Math.abs(currentY.current - targetY.current) > 0.01
    ) {
      rafRef.current = requestAnimationFrame(animate);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = rootRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    rootRef.current!.style.setProperty("--mouse-x", `${x}px`);
    rootRef.current!.style.setProperty("--mouse-y", `${y}px`);

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    targetX.current = ((x - centerX) / centerX) * tilt;
    targetY.current = ((y - centerY) / centerY) * -tilt;

    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
  };

  const handleMouseEnter = () => {
    if (spotRef.current) spotRef.current.style.opacity = "1";
  };

  const handleMouseLeave = () => {
    if (spotRef.current) spotRef.current.style.opacity = "0";
    targetX.current = 0;
    targetY.current = 0;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(animate);
  };

  return (
    <div
      ref={rootRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`card-spotlight ${className}`}
      style={{ position: "relative", overflow: "hidden" }}
    >
      {children}
      <div ref={spotRef} style={spotlightStyle} />
    </div>
  );
};

export default SpotlightCard;
